import { logClientEvent, describeError } from './jsErrorLogger.js';

export default function api({ token = null, csrf = null } = {}) {
  let authToken = token;
  let csrfToken = csrf;

  const setToken = (newToken) => {
    authToken = newToken;
  };

  const setCSRF = (newCsrf) => {
    csrfToken = newCsrf;
  };

  const getCSRFToken = () => {
    if (!csrfToken) {
      csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    }
    return csrfToken;
  };

  // const buildQueryString = (params) => {
  //   return new URLSearchParams(params).toString();
  // };
  const buildQueryString = (obj, prefix = '') => {
    const query = [];

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        const paramKey = prefix ? `${prefix}[${key}]` : key;

        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
          query.push(buildQueryString(value, paramKey));
        } else if (Array.isArray(value)) {
          value.forEach((val) => {
            query.push(`${encodeURIComponent(paramKey)}[]=${encodeURIComponent(val)}`);
          });
        } else {
          query.push(`${encodeURIComponent(paramKey)}=${encodeURIComponent(value)}`);
        }
      }
    }

    return query.join('&');
  };

  const handleResponse = async (response) => {
    const contentType = response.headers.get('Content-Type');
    const isJson = contentType && contentType.includes('application/json');

    // Citim mereu ca text și parsăm noi. response.json() pe un corp murdar (o notiță PHP
    // tipărită înaintea JSON-ului) sau trunchiat arunca o excepție FĂRĂ status, pe care
    // confirm.js o afișa ca „verifică conexiunea” deși serverul răspunsese 200 — iar omul
    // reîncerca o programare deja făcută. Acum: JSON-ul se recuperează de după gunoi când se
    // poate (și gunoiul se raportează), iar când nu se poate, eroarea poartă statusul și corpul,
    // ca mailul de alertă să spună ce a fost.
    const text = await response.text();
    let data = text;
    if (isJson) {
      try {
        data = JSON.parse(text);
      } catch (parseErr) {
        const start = text.indexOf('{');
        let salvaged = null;
        if (start > 0) {
          try { salvaged = JSON.parse(text.slice(start)); } catch (e) { /* rămâne null */ }
        }
        if (salvaged === null) {
          throw { status: response.status, data: text.slice(0, 300), parseError: parseErr.message };
        }
        logClientEvent({ message: 'json salvaged from noisy body', status: response.status, step: 'api', context: text.slice(0, Math.min(start, 300)) });
        data = salvaged;
      }
    }

    if (!response.ok) {
      throw { status: response.status, data };

      // TODO:
      // const error = new Error(
      //     data?.error || data?.message || response.statusText
      // );
      //
      // error.status = response.status;
      // error.data = data;
      // error.isServerError = true;
      //
      // throw error;
    }

    return data;
  };

  // Ce merita reincercat in tacere: cererea nu a primit NICIUN raspuns (retea picata, conexiune
  // taiata sub ea - pe iOS apare ca „TypeError: Load failed"), sau balansorul a raspuns 502/503/504,
  // adica nici n-a ajuns la PHP. Un 4xx sau un 500 sunt raspunsuri adevarate, nu accidente de drum,
  // si se reincearca doar cu stirea omului. AbortError e propriul nostru timeout: iese ca pana acum.
  const isTransient = (err) => {
    const status = err && err.status;
    if (status) {
      return status === 502 || status === 503 || status === 504;
    }
    return !(err && err.name === 'AbortError');
  };

  // timeoutMs: fara el, un GET care atarna (incalzirea / recuperarea de la Finalize) ar tine
  // butonul blocat la nesfarsit. AbortController -> fetch respinge cu AbortError -> error().
  // retry: ETICHETA cererii, pusa DOAR de apelantul care stie ca a lui se poate repeta fara urmari
  // (o citire). Trimiterea codului sau finalizarea nu primesc eticheta: ar insemna doua SMS-uri sau
  // doua programari. Eticheta ajunge in telemetrie, ca sa stim cat de des ne salveaza reincercarea.
  const request = async (method, url, { data = {}, headers = {}, success, error, timeoutMs = 0, retry = null } = {}) => {
    let options = {
      method: method.toUpperCase(),
      headers: {
        // Accept flips Laravel's expectsJson(): errors come back as JSON
        // (419 {"message":...}, 422 {message, errors}) instead of HTML error
        // pages / 302-redirects-followed-to-200-HTML that the widget used to
        // swallow silently or mistake for success.
        Accept: 'application/json',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          ...(getCSRFToken() ? { 'X-CSRF-Token': getCSRFToken() } : {}),
        ...headers
      }
    };

    if (method === 'GET') {
      const query = buildQueryString(data);
      url += query ? (url.includes('?') ? '&' : '?') + query : '';
    } else {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(data);
    }

    let result;
    let ok = false;
    let firstError = null;

    // Fiecare incercare isi face propriul cronometru: semnalul unui AbortController deja folosit
    // ar opri instantaneu incercarea urmatoare.
    for (let attempt = 0; ; attempt++) {
      let timer = null;
      if (timeoutMs > 0 && typeof AbortController !== 'undefined') {
        const controller = new AbortController();
        options.signal = controller.signal;
        timer = setTimeout(() => controller.abort(), timeoutMs);
      }

      try {
        const response = await fetch(url, options);
        result = await handleResponse(response);
        ok = true;
      } catch (err) {
        if (retry && attempt === 0 && isTransient(err)) {
          if (timer) clearTimeout(timer);
          firstError = err;
          await new Promise((resolve) => setTimeout(resolve, 500));
          continue;
        }
        if (typeof error === 'function') error(err);
      }

      if (timer) clearTimeout(timer);
      break;
    }

    if (ok && firstError) {
      // Omul n-a vazut nimic; noi vrem sa stim cat de des ne salveaza a doua incercare.
      logClientEvent({
        message: 'request retried ok',
        status: firstError.status ?? null,
        step: String(retry),
        context: describeError(firstError)
      });
    }
    // OUTSIDE the try: an exception thrown inside the success callback must
    // surface as a real error (and reach jsErrorLogger), not be silently
    // re-routed into the error callback as if the request itself had failed —
    // that rerouting is how a successful booking could render as "nothing".
    if (ok && typeof success === 'function') success(result);
  };

  return {
    get: (url, options = {}) => request('GET', url, options),
    post: (url, options = {}) => request('POST', url, options),
    setToken,
    setCSRF
  };
}
